// Aprendizado contínuo (Parte C) — painel /ai-learning (só admin; a API responde 403 para os demais).
// Rotas: GET/PUT /ai-learning/settings, GET /ai-learning/lessons, PUT /ai-learning/lessons/:id,
// GET /ai-learning/report. A empresa é sempre a da sessão (o backend ignora companyId).
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress, Switch } from "@material-ui/core";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import RefreshIcon from "@material-ui/icons/Refresh";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import RobotIcon from "@material-ui/icons/Android";
import moment from "moment";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import { PageHeader, StatCard, Section, MainContainer } from "../../components/ui";
import { getTokens } from "../../styles/tokens";
import LearningReport from "./LearningReport";
import LessonsTable from "./LessonsTable";
import { fmtPct, lastRunLines } from "./format";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    scroll: { flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", ...theme.scrollbarStyles },
    page: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(3),
      maxWidth: 1280,
      width: "100%",
      marginLeft: "auto",
      marginRight: "auto",
      paddingBottom: theme.spacing(2),
    },
    pageHeader: { paddingTop: 0, paddingBottom: 0 },
    settingsGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
      gap: 24,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    toggleRow: { display: "flex", alignItems: "flex-start", gap: 8 },
    toggleText: { minWidth: 0, paddingTop: 7 },
    toggleLabel: { fontWeight: 600, fontSize: 14, color: t.text },
    toggleHint: { fontSize: 13, color: t.textSecondary, marginTop: 2, lineHeight: 1.45 },
    lastRun: {
      borderLeft: `1px solid ${t.line}`,
      paddingLeft: 24,
      [theme.breakpoints.down("sm")]: { borderLeft: 0, paddingLeft: 0, borderTop: `1px solid ${t.line}`, paddingTop: 16 },
    },
    lastRunTitle: { fontWeight: 600, fontSize: 14, color: t.text, marginBottom: 4 },
    lastRunDate: { fontSize: 12, color: t.textMuted, marginBottom: 6 },
    lastRunLine: { fontSize: 13, color: t.textSecondary, lineHeight: 1.5 },
    note: {
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      marginTop: 16,
      padding: "10px 12px",
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
      border: `1px solid ${t.line}`,
      fontSize: 13,
      color: t.textSecondary,
      lineHeight: 1.45,
      "& svg": { fontSize: 18, flex: "none", marginTop: 1, color: t.textMuted },
    },
    noteWarn: {
      backgroundColor: t.status.warn.bg,
      borderColor: "transparent",
      color: t.status.warn.fg,
      "& svg": { color: t.status.warn.fg },
    },
    loadingRow: { display: "flex", justifyContent: "center", padding: 48 },
  };
});

const AiLearning = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const allowed = user?.profile === "admin";

  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(null);
  const [report, setReport] = useState(null);
  const [lessons, setLessons] = useState([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleError = useCallback((err) => {
    if (err?.response?.status === 403) {
      if (mountedRef.current) setForbidden(true);
      return;
    }
    toastError(err);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      api.get("/ai-learning/settings"),
      api.get("/ai-learning/report"),
      api.get("/ai-learning/lessons"),
    ]);
    if (!mountedRef.current) return;
    const [s, r, l] = results;
    const firstError = results.find((x) => x.status === "rejected");
    if (s.status === "fulfilled") setSettings(s.value.data || null);
    if (r.status === "fulfilled") setReport(r.value.data || null);
    if (l.status === "fulfilled") setLessons(Array.isArray(l.value.data?.lessons) ? l.value.data.lessons : []);
    if (firstError) handleError(firstError.reason);
    setLoading(false);
  }, [handleError]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const saveSetting = async (field, value) => {
    setSaving(field);
    try {
      const { data } = await api.put("/ai-learning/settings", { [field]: value });
      if (mountedRef.current) setSettings(data || null);
      toast.success(i18n.t("aiLearning.settings.saved"));
    } catch (err) {
      handleError(err);
    } finally {
      if (mountedRef.current) setSaving(null);
    }
  };

  const updateLesson = async (id, status) => {
    try {
      await api.put(`/ai-learning/lessons/${id}`, { status });
      if (!mountedRef.current) return;
      setLessons((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(i18n.t("aiLearning.lessons.updated"));
    } catch (err) {
      handleError(err);
    }
  };

  const weekly = useMemo(() => (Array.isArray(report?.weekly) ? report.weekly : []), [report]);

  const cards = useMemo(() => {
    const cur = weekly[weekly.length - 1];
    const prev = weekly[weekly.length - 2];
    const totalEvaluated = weekly.reduce((acc, w) => acc + (Number(w.total) || 0), 0);
    const share = report?.aiShare || { ai: 0, human: 0, mixed: 0, bot: 0 };
    const shareTotal = (share.ai || 0) + (share.human || 0) + (share.mixed || 0) + (share.bot || 0);
    const pctOf = (n) => fmtPct(shareTotal ? (n || 0) / shareTotal : null);
    let trend = null;
    if (cur?.total && prev?.total) {
      const diff = Math.round((cur.rate - prev.rate) * 100);
      trend = { value: diff, label: i18n.t("aiLearning.cards.pp", { value: `${diff > 0 ? "+" : ""}${diff}` }) };
    }
    const active = lessons.filter((l) => l.status === "active").length;
    const pending = lessons.filter((l) => l.status === "pending").length;
    return [
      {
        key: "success",
        label: i18n.t("aiLearning.cards.successRate"),
        value: cur?.total ? fmtPct(cur.rate) : "—",
        hint: trend ? i18n.t("aiLearning.cards.vsLastWeek") : cur?.total ? undefined : i18n.t("aiLearning.cards.noData"),
        trend,
        icon: <TrendingUpIcon />,
        tone: "success",
      },
      {
        key: "ai",
        label: i18n.t("aiLearning.cards.aiShare"),
        value: shareTotal ? pctOf((share.ai || 0) + (share.mixed || 0)) : "—",
        hint: shareTotal
          ? i18n.t("aiLearning.cards.aiShareHint", { ai: pctOf(share.ai), mixed: pctOf(share.mixed) })
          : i18n.t("aiLearning.cards.noData"),
        icon: <RobotIcon />,
        tone: "brand",
      },
      {
        key: "evaluated",
        label: i18n.t("aiLearning.cards.evaluated"),
        value: totalEvaluated,
        hint: i18n.t("aiLearning.cards.evaluatedHint", { weeks: weekly.length || 8 }),
        icon: <ForumOutlinedIcon />,
        tone: "info",
      },
      {
        key: "lessons",
        label: i18n.t("aiLearning.cards.activeLessons"),
        value: active,
        hint: pending ? i18n.t("aiLearning.cards.pendingHint", { count: pending }) : undefined,
        icon: <EmojiObjectsOutlinedIcon />,
        tone: pending ? "warning" : "neutral",
      },
    ];
  }, [weekly, report, lessons]);

  if (!allowed || forbidden) return <ForbiddenPage />;

  const lastRun = settings?.lastRun || null;
  const runLines = lastRunLines(lastRun);

  return (
    <MainContainer>
      <div className={classes.scroll}>
        <div className={classes.page}>
          <PageHeader
            className={classes.pageHeader}
            title={i18n.t("aiLearning.title")}
            subtitle={i18n.t("aiLearning.subtitle")}
            actions={
              <Button variant="outlined" onClick={load} startIcon={<RefreshIcon />} disabled={loading}>
                {i18n.t("aiLearning.refresh")}
              </Button>
            }
          />

          {loading && !settings ? (
            <div className={classes.loadingRow}>
              <CircularProgress size={28} />
            </div>
          ) : (
            <>
              <Section title={i18n.t("aiLearning.settings.title")}>
                <div className={classes.settingsGrid}>
                  <div>
                    <div className={classes.toggleRow}>
                      <Switch
                        color="primary"
                        checked={Boolean(settings?.enabled)}
                        disabled={!settings || saving !== null}
                        onChange={(e) => saveSetting("enabled", e.target.checked)}
                        inputProps={{ "aria-label": i18n.t("aiLearning.settings.enabled"), "data-testid": "toggle-enabled" }}
                      />
                      <div className={classes.toggleText}>
                        <div className={classes.toggleLabel}>{i18n.t("aiLearning.settings.enabled")}</div>
                        <div className={classes.toggleHint}>{i18n.t("aiLearning.settings.enabledHint")}</div>
                      </div>
                    </div>
                    <div className={classes.toggleRow} style={{ marginTop: 12 }}>
                      <Switch
                        color="primary"
                        checked={Boolean(settings?.approval)}
                        disabled={!settings || saving !== null}
                        onChange={(e) => saveSetting("approval", e.target.checked)}
                        inputProps={{ "aria-label": i18n.t("aiLearning.settings.approval"), "data-testid": "toggle-approval" }}
                      />
                      <div className={classes.toggleText}>
                        <div className={classes.toggleLabel}>{i18n.t("aiLearning.settings.approval")}</div>
                        <div className={classes.toggleHint}>{i18n.t("aiLearning.settings.approvalHint")}</div>
                      </div>
                    </div>
                  </div>
                  <div className={classes.lastRun}>
                    <div className={classes.lastRunTitle}>{i18n.t("aiLearning.lastRun.title")}</div>
                    {lastRun ? (
                      <>
                        <div className={classes.lastRunDate}>
                          {lastRun.at ? moment(lastRun.at).format("DD/MM/YYYY HH:mm") : "—"}
                        </div>
                        {runLines.map((line, i) => (
                          <div key={i} className={classes.lastRunLine}>
                            {line}
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className={classes.lastRunLine}>{i18n.t("aiLearning.lastRun.never")}</div>
                    )}
                  </div>
                </div>
                {settings && settings.hasAgent === false && (
                  <div className={`${classes.note} ${classes.noteWarn}`}>
                    <InfoOutlinedIcon />
                    <span>{i18n.t("aiLearning.settings.noAgent")}</span>
                  </div>
                )}
                <div className={classes.note}>
                  <InfoOutlinedIcon />
                  <span>{i18n.t("aiLearning.settings.cost")}</span>
                </div>
              </Section>

              <Grid2 container spacing={2}>
                {cards.map((c) => (
                  <Grid2 xs={12} sm={6} lg={3} key={c.key}>
                    <StatCard label={c.label} value={c.value} hint={c.hint} trend={c.trend} icon={c.icon} tone={c.tone} />
                  </Grid2>
                ))}
              </Grid2>

              <LearningReport report={report} />

              <LessonsTable lessons={lessons} onUpdate={updateLesson} />
            </>
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default AiLearning;
